import React, { useCallback, useRef, useState } from 'react';
import { useToast } from '../../components/common';
import api from '../../services/api';
import './AppLogoField.css';

/**
 * The app's logo — uploaded, or the generated mark when there is none.
 *
 * An app had no logo at all: not a column, not a field in any payload. Every app
 * in this list and every app on a consent screen drew the same dark lattice with
 * the first two letters of its name, so two apps beginning "Mi" were the same
 * picture and a developer had no way to be recognised by the people deciding
 * whether to trust them.
 *
 * THE FALLBACK STAYS. It is not a placeholder to be embarrassed about — it is
 * what an app without a logo looks like, forever, and most apps will never have
 * one. It is drawn by the same `.app-icon-grid` the list card uses, so the mark
 * a developer sees here is the mark a user sees there.
 *
 * NOT STAGED, and deliberately. Everything else in Declare collects into the
 * pending change and goes live on commit, because it alters what the app IS or
 * what it asks for. A logo alters neither: rolling a permission back should not
 * change a developer's branding, and swapping a picture should not cut a version
 * or ask what happens to the 128 people who granted the old one. So it applies
 * immediately, and says so.
 *
 * THE POINTER, NOT THE URL. Upload puts the bytes in storage and creates a file
 * node; what the app stores is that node's id. The URL comes back signed and
 * expires in an hour, so a URL written into the app record would break an hour
 * later — which is exactly the bug `groups.avatar_url` had before migration 240.
 */

/** Small enough to send, large enough to look right on a consent card. */
const MAX_BYTES = 2 * 1024 * 1024;
const ACCEPT = 'image/png,image/jpeg,image/svg+xml,image/webp';

function AppLogoField({ app, onSaved }) {
  const toast = useToast();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  // The signed URL from the last load. Replaced optimistically after an upload so
  // the new mark appears without waiting for a refetch.
  const [preview, setPreview] = useState(app.logo_url || null);

  const initials = (app.name || '?').trim().slice(0, 2).toUpperCase();

  const setLogo = useCallback(async (fileId) => {
    await api.updateThirdParty(app.id, { logo_file_id: fileId });
    await onSaved?.();
  }, [app.id, onSaved]);

  const pick = useCallback(async (e) => {
    const file = e.target.files?.[0];
    // The input keeps its value, so choosing the same file twice would not fire
    // again without this.
    e.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('That is not an image.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`That file is ${(file.size / 1024 / 1024).toFixed(1)}MB. The limit is 2MB.`);
      return;
    }

    setBusy(true);
    // Shown immediately, from the bytes in the browser — the signed URL takes a
    // round trip and a blank square in the meantime reads as a failure.
    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);
    try {
      const node = await api.uploadFileAsNode(file, { purpose: 'app_logo' });
      const fileId = node?.id || node?.node?.id;
      if (!fileId) throw new Error('The file uploaded but no id came back.');
      await setLogo(fileId);
      toast.success('Logo updated.');
    } catch (err) {
      setPreview(app.logo_url || null);
      toast.error(err.message || 'Could not set the logo.');
    } finally {
      // Revoked after the swap, not before: releasing it while it is still the
      // src is what makes the image blink out.
      setTimeout(() => URL.revokeObjectURL(localUrl), 1000);
      setBusy(false);
    }
  }, [app.logo_url, setLogo, toast]);

  const remove = useCallback(async () => {
    setBusy(true);
    try {
      await setLogo(null);
      setPreview(null);
      toast.success('Logo removed. The generated mark is back.');
    } catch (err) {
      toast.error(err.message || 'Could not remove the logo.');
    } finally {
      setBusy(false);
    }
  }, [setLogo, toast]);

  return (
    <div className="applogo">
      <div className="applogo__mark">
        {preview ? (
          <img
            className="applogo__img"
            src={preview}
            alt=""
            /* A signed URL expires in an hour. Rather than showing a broken
               image to somebody who left the tab open, fall back to the mark —
               which is what this app looks like without a logo anyway. */
            onError={() => setPreview(null)}
          />
        ) : (
          <span className="applogo__fallback app-icon-grid" aria-hidden="true">
            <span className="applogo__initials">{initials}</span>
          </span>
        )}
      </div>

      <div className="applogo__body">
        <span className="applogo__label">Logo</span>
        <p className="applogo__note">
          {preview
            ? 'Shown on the consent screen and in the app store.'
            : 'No logo, so this app is drawn from its initials. Shown on the consent screen and in the app store.'}
        </p>
        <div className="applogo__actions">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            onChange={pick}
            className="applogo__input"
            aria-label="Choose a logo image"
          />
          <button
            type="button"
            className="applogo__btn"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
          >
            {busy ? 'Working…' : preview ? 'Replace' : 'Upload'}
          </button>
          {preview && (
            <button type="button" className="applogo__btn applogo__btn--quiet" disabled={busy} onClick={remove}>
              Remove
            </button>
          )}
          <span className="applogo__hint">PNG, JPEG, SVG or WebP, up to 2MB</span>
        </div>
      </div>
    </div>
  );
}

export default AppLogoField;
